import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useCategory } from '../context/CategoryContext.js'
import { useCart } from '../context/CartContext.js'

export default function Header() {
  const { categories } = useCategory()
  const { cart } = useCart()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const path = pathname

  const [showSearch, setShowSearch] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const [openMenu, setOpenMenu] = useState(null) // 'produtos' | 'conta' | null
  const [userInfo, setUserInfo] = useState(() => readUserInfo())

  function readUserInfo() {
    try {
      const raw = localStorage.getItem('user')
      if (!raw) return null
      const u = JSON.parse(raw)
      const name =
        u?.first_name ||
        u?.nome ||
        u?.name ||
        (u?.email ? u.email.split('@')[0] : null)
      const tipo = u?.tipo || u?.role || null
      return { name, tipo }
    } catch {
      return null
    }
  }

  const produtosActive = /^\/(produtos|categorias)(\b|\/)/.test(path)

  useEffect(() => {
    if (showSearch && inputRef.current) inputRef.current.focus()
  }, [showSearch])

  // fecha dropdowns ao clicar fora
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenMenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Ouve alterações de sessão (custom event + storage)
  useEffect(() => {
    const loadUser = () => setUserInfo(readUserInfo())
    const handleStorage = (e) => {
      if (e.key === 'user' || e.key === 'authToken') loadUser()
    }
    const handleAuthEvent = () => loadUser()
    window.addEventListener('storage', handleStorage)
    window.addEventListener('auth:updated', handleAuthEvent)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('auth:updated', handleAuthEvent)
    }
  }, [])

  const toggleMenu = (menu) => setOpenMenu((prev) => (prev === menu ? null : menu))
  const closeMenus = () => setOpenMenu(null)

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
    setUserInfo(null)
    window.dispatchEvent(new Event('auth:updated'))
    closeMenus()
    navigate('/login')
  }

  // Recarrega user quando a rota muda (após login)
  useEffect(() => {
    setUserInfo(readUserInfo())
  }, [pathname])

  const userName = userInfo?.name || null
  const isProducer = (userInfo?.tipo || '').toUpperCase() === 'P'

  function submitSearch(e) {
    e?.preventDefault?.()
    const q = String(query || '').trim()
    setShowSearch(false)
    if (q) navigate(`/categorias?q=${encodeURIComponent(q)}`)
    else navigate('/categorias')
  }

  return (
    <>
      <header className="header">
        <div className="container" ref={containerRef}>
          <div className="logo">
            <Link to="/">
              <img src="/images/logo.png" alt="SIDES" className="logo-image" />
              <h1>SIDES</h1>
            </Link>
          </div>

          <nav className="nav">
            <Link to="/" className={path === '/' ? 'active' : ''}>Início</Link>
            
            <details className="dropdown" data-active={produtosActive} open={openMenu === 'produtos'}>
              <summary onClick={(e) => { e.preventDefault(); toggleMenu('produtos') }}>Produtos</summary>
              <div className="submenu">
                {categories.map(category => (
                  <Link key={category.id} to={`/categorias/${category.slug}`} onClick={closeMenus}>
                    {category.nome}
                  </Link>
                ))}
              </div>
            </details>

            {isProducer && (
              <Link to="/produtor/dashboard" className={path.startsWith('/produtor') ? 'active' : ''}>Dashboard</Link>
            )}

            <Link to="/sobre" className={path === '/sobre' ? 'active' : ''}>Sobre</Link>
            <Link to="/ajuda" className={path === '/ajuda' ? 'active' : ''}>Ajuda</Link>
          </nav>

          <div className="nav-actions">
            <button className="icon-btn" aria-label="Pesquisar" onClick={() => setShowSearch(s=>!s)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
            <Link to="/carrinho" className="cart-btn" aria-label="Carrinho">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 12.39a2 2 0 0 0 2 1.61h7.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {(cart?.length || 0) > 0 && (
                <span className="cart-count" aria-live="polite">{cart.length}</span>
              )}
            </Link>
            <details className="dropdown" open={openMenu === 'conta'}>
              <summary onClick={(e) => { e.preventDefault(); toggleMenu('conta') }}>
                {userName || 'Registrar'}
              </summary>
              <div className="submenu">
                {userName ? (
                  <>
                    <button type="button" className="ghost-button" onClick={handleLogout}>Sair</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeMenus}>Entrar</Link>
                    <Link to="/registro" onClick={closeMenus}>Criar Conta</Link>
                    <Link to="/produtor/cadastro" onClick={closeMenus}>Sou Produtor</Link>
                  </>
                )}
              </div>
            </details>
          </div>
        </div>
      </header>

      {showSearch && (
        <div className="header-search-panel" role="dialog" aria-modal="true">
          <div className="container">
            <form className="header-search-form" onSubmit={submitSearch}>
              <span className="search-icon" aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </span>
              <input
                ref={inputRef}
                type="search"
                className="header-search-input"
                placeholder="Pesquisar produtos..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setShowSearch(false) }}
              />
              <button type="button" className="header-search-close" onClick={() => setShowSearch(false)} aria-label="Fechar pesquisa">×</button>
              <button type="submit" className="search-button">Pesquisar</button>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
